import { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { Plus, Printer, Trash2, Barcode as BarcodeIcon, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function Barcode({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    try {
      JsBarcode(ref.current, value, {
        format: "CODE128",
        displayValue: false,
        margin: 12,
        height: 70,
        width: 3,
        background: "#ffffff",
        lineColor: "#000000",
      });
    } catch {}
  }, [value]);
  return <svg ref={ref} className="tray-barcode-svg" />;
}

function Label({ value }) {
  return (
    <div className="tray-label" data-testid={`tray-label-${value}`}>
      <img src="/orse-logo.png" alt="ORSE GOLD" className="tray-label-logo" />
      <Barcode value={value} />
      <div className="tray-label-code">{value}</div>
    </div>
  );
}

export default function TrayBarcodes() {
  const [entry, setEntry] = useState("");
  const [trays, setTrays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/trays")
      .then((r) => setTrays(r.data))
      .finally(() => setLoading(false));
  }, []);

  const add = async () => {
    const v = entry.trim().toUpperCase();
    if (!v) return;
    if (trays.some((t) => t.name === v)) { toast.error("Bu tabla zaten listede"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/trays", { name: v });
      setTrays((t) => [...t, data]);
      setEntry("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tray) => {
    const prev = trays;
    setTrays((t) => t.filter((x) => x.id !== tray.id));
    try {
      await api.delete(`/admin/trays/${tray.id}`);
    } catch (err) {
      toast.error("Silinemedi");
      setTrays(prev);
    }
  };

  const print = () => {
    if (trays.length === 0) { toast.error("Önce en az bir tabla ekleyin"); return; }
    const logoSrc = `${window.location.origin}/orse-logo.png`;
    const cells = trays.map((t) => {
      let png = "";
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, t.name, { format: "CODE128", displayValue: false, margin: 12, height: 70, width: 3 });
        png = canvas.toDataURL("image/png");
      } catch {}
      return `<div class="label"><img class="logo" src="${logoSrc}"/><img class="bc" src="${png}"/><div class="code">${t.name}</div></div>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Tabla Barkodları</title>
      <style>
        @page { size: A4; margin: 10mm; }
        body { margin: 0; font-family: monospace; background: #fff; }
        .sheet { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8mm; padding: 4mm; }
        .label { border: 1px dashed #cbb26b; border-radius: 4px; padding: 8px 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; break-inside: avoid; page-break-inside: avoid; }
        .logo { height: 22px; }
        .bc { max-width: 100%; height: auto; }
        .code { font-size: 14px; font-weight: 700; letter-spacing: 2px; color: #111; }
      </style></head>
      <body><div class="sheet">${cells}</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},400);};<\/script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Yazdırma penceresi açılamadı. Tarayıcıda açılır pencere (pop-up) iznini verin."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div data-testid="tray-barcodes-page">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #tray-print-sheet, #tray-print-sheet * { visibility: visible !important; }
          #tray-print-sheet { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .tray-no-print { display: none !important; }
          @page { size: A4; margin: 10mm; }
        }
        #tray-print-sheet {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8mm;
        }
        .tray-label {
          border: 1px dashed #cbb26b;
          border-radius: 4px;
          padding: 8px 6px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          background: #ffffff;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .tray-label-logo { height: 22px; width: auto; object-fit: contain; }
        .tray-barcode-svg { max-width: 100%; height: auto; display: block; margin: 0 auto; image-rendering: crisp-edges; }
        .tray-label-code { font-size: 14px; font-weight: 700; letter-spacing: 2px; color: #111; font-family: monospace; }
      `}</style>

      <div className="tray-no-print">
        <div className="flex items-center gap-2 mb-1">
          <BarcodeIcon className="text-gold" size={22} />
          <h1 className="font-display text-3xl text-foreground">Tabla Barkodları</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Tabla adını yazıp ekleyin, ardından yazdırın. Barkodlar A4 sayfada etiket ızgarası olarak basılır ve kamera/el okuyucu ile taranabilir.
        </p>

        <div className="flex gap-2 max-w-md mb-4">
          <Input
            data-testid="tray-name-input"
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Örn. TABLA-01"
            className="bg-elevated border-border"
          />
          <button
            data-testid="tray-add-button"
            onClick={add}
            disabled={saving}
            className="shrink-0 px-4 rounded-md bg-gold text-obsidian font-medium flex items-center gap-1 text-sm hover:bg-gold/90 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Ekle
          </button>
        </div>

        {loading ? (
          <div className="flex py-6"><Loader2 className="animate-spin text-gold" size={22} /></div>
        ) : trays.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="tray-chip-list">
            {trays.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-2 px-3 h-8 rounded-full bg-elevated border border-border text-sm text-foreground">
                {t.name}
                <button data-testid={`tray-remove-${t.name}`} onClick={() => remove(t)} className="text-muted-foreground hover:text-red-400 transition-colors">
                  <Trash2 size={14} />
                </button>
              </span>
            ))}
          </div>
        )}

        <button
          data-testid="tray-print-button"
          onClick={print}
          disabled={trays.length === 0}
          className="px-5 h-11 rounded-md border border-gold text-gold font-medium flex items-center gap-2 text-sm hover:bg-gold/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed mb-8"
        >
          <Printer size={18} /> Yazdır ({trays.length})
        </button>
      </div>

      {trays.length === 0 ? (
        <p className="tray-no-print text-muted-foreground text-sm py-12 text-center border border-dashed border-border rounded-lg">
          Henüz tabla eklenmedi. Yukarıdan bir tabla adı ekleyin.
        </p>
      ) : (
        <div id="tray-print-sheet" data-testid="tray-print-sheet">
          {trays.map((t) => <Label key={t.id} value={t.name} />)}
        </div>
      )}
    </div>
  );
}
